import { AfterViewInit, Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { GlobalConstants } from '../global-constants';
import { QueryService } from '../comun/services/query.service';
import { GeoService } from '../comun/services/geo.service';
import { ToastrService } from 'ngx-toastr';
import { LoaderService } from '../comun/services/loader.service';
import L from 'leaflet';
import { Subscription } from 'rxjs';

interface RadarPerfil {
  ID: number | string;
  first_name?: string;
  nombre?: string;
  slug?: string;
  foto_principal_thumb?: string;
  foto_principal_grande?: string;
  latitud: number | string;
  longitud: number | string;
  online?: boolean | number | string;
  mascota?: { ma_nombre?: string } | null;
  distanciaCalculada?: number | null;
}

@Component({
  selector: 'app-radar',
  templateUrl: './radar.component.html',
  styleUrls: ['./radar.component.css']
})
export class RadarComponent implements AfterViewInit, OnDestroy {
  @ViewChild('mapContainer', { static: true }) mapContainer!: ElementRef<HTMLDivElement>;

  map?: L.Map;
  misdatos: any = {};
  perfiles: RadarPerfil[] = [];
  perfilesVisibles: RadarPerfil[] = [];
  miLat = 0;
  miLng = 0;
  ubicacionDisponible = false;
  // Coordenadas guardadas en el perfil: nunca deben ser reemplazadas por el GPS.
  zonaLat = 0;
  zonaLng = 0;
  zonaDisponible = false;
  soloOnline = false;
  cargando = false;
  buscandoUbicacion = false;
  aviso = '';

  private marcadores = L.layerGroup();
  private radarCirculos = L.layerGroup();
  private marcadorPropio?: L.CircleMarker;
  private subs = new Subscription();
  private solicitud?: Subscription;
  private secuenciaBusqueda = 0;
  private zoomInicial = 12;
  private destroyed = false;
  private readonly defaultCenter: L.LatLngTuple = [28.4636, -16.2518];

  constructor(
    private query: QueryService,
    private geoService: GeoService,
    private toastr: ToastrService,
    private loader: LoaderService
  ) {}

  get conectadosVisibles(): number {
    return this.perfilesVisibles.filter(p => this.estaOnline(p)).length;
  }

  ngAfterViewInit(): void {
    this.crearMapa();
    this.cargarUsuario();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.secuenciaBusqueda++;
    this.solicitud?.unsubscribe();
    this.subs.unsubscribe();
    this.map?.off();
    this.map?.remove();
    this.map = undefined;
  }

  private crearMapa(): void {
    if (this.map) return;
    this.map = L.map(this.mapContainer.nativeElement, { zoomControl: false });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 19
    }).addTo(this.map);
    L.control.zoom({ position: 'bottomright' }).addTo(this.map);
    this.marcadores.addTo(this.map);
    this.radarCirculos.addTo(this.map);
    this.map.setView(this.defaultCenter, 8);
    this.map.on('moveend', () => this.buscarPerfiles());
    // El contenedor puede estar dentro de pestañas o elementos WordPress que
    // terminan de dimensionarse después de ngAfterViewInit.
    setTimeout(() => { if (!this.destroyed) this.map?.invalidateSize(); }, 100);
  }

  private cargarUsuario(): void {
    this.loader.show();
    const sub = this.query.query('misdatos', 'GET', { ID: 0 }).subscribe({
      next: (respuesta: any) => {
        this.loader.hide();
        this.misdatos = Array.isArray(respuesta) ? (respuesta[0] || {}) : (respuesta?.registros && !Array.isArray(respuesta.registros) ? respuesta.registros : respuesta || {});
        const lat = Number(this.misdatos?.geolatitud);
        const lon = Number(this.misdatos?.geolongitud);
        if (this.coordenadasValidas(lat, lon)) {
          this.zonaLat = lat;
          this.zonaLng = lon;
          this.zonaDisponible = true;
          this.fijarMiUbicacion(lat, lon, true);
        } else {
          this.aviso = 'Sin ubicación guardada: utiliza «Mi ubicación exacta» para buscar cerca de ti.';
          this.buscarPerfiles();
        }
      },
      error: () => {
        this.loader.hide();
        this.aviso = 'No se pudo cargar tu perfil. Comprueba que has iniciado sesión.';
      }
    });
    this.subs.add(sub);
  }

  private coordenadasValidas(lat: number, lon: number): boolean {
    return Number.isFinite(lat) && Number.isFinite(lon) && lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180 && !(lat === 0 && lon === 0);
  }

  private fijarMiUbicacion(lat: number, lon: number, centrar = false): void {
    if (!this.coordenadasValidas(lat, lon)) return;
    this.miLat = lat;
    this.miLng = lon;
    this.ubicacionDisponible = true;
    this.aviso = '';
    this.pintarRadar();
    if (centrar) this.map?.setView([lat, lon], this.zoomInicial);
    else this.buscarPerfiles();
  }

  volverAMiZona(): void {
    // Volver a la dirección del perfil, incluso si antes se utilizó el GPS.
    if (!this.zonaDisponible || !this.map) return;
    this.fijarMiUbicacion(this.zonaLat, this.zonaLng, true);
  }

  solicitarUbicacion(): void {
    if (this.buscandoUbicacion) return;
    this.buscandoUbicacion = true;
    // El permiso se pide solo tras pulsar el botón, sin solicitarlo al cargar.
    const sub = this.geoService.getExactPosition().subscribe({
      next: position => {
        this.buscandoUbicacion = false;
        this.fijarMiUbicacion(position.coords.latitude, position.coords.longitude, true);
      },
      error: () => {
        this.buscandoUbicacion = false;
        this.toastr.warning('No se pudo acceder a tu ubicación. Revisa los permisos del navegador.');
      }
    });
    this.subs.add(sub);
  }

  private pintarRadar(): void {
    this.radarCirculos.clearLayers();
    if (this.marcadorPropio) { this.map?.removeLayer(this.marcadorPropio); this.marcadorPropio = undefined; }
    if (!this.ubicacionDisponible || !this.map) return;
    const punto: L.LatLngTuple = [this.miLat, this.miLng];
    [1000, 3000, 5000].forEach((metros, i) => {
      L.circle(punto, {
        radius: metros, color: '#527b69', weight: 1.4, opacity: i === 0 ? .65 : .35,
        fillColor: '#527b69', fillOpacity: i === 0 ? .09 : .025,
        interactive: false
      }).addTo(this.radarCirculos);
    });
    this.marcadorPropio = L.circleMarker(punto, {
      radius: 9, color: '#fff', weight: 3, fillColor: '#315e49', fillOpacity: 1
    }).addTo(this.map).bindPopup('<strong>Tu ubicación de referencia</strong>');
  }

  buscarPerfiles(): void {
    if (!this.map || this.misdatos?.perfilPublico === 'no_publico') return;
    const b = this.map.getBounds();
    if (!b.isValid()) return;
    this.solicitud?.unsubscribe();
    const n = ++this.secuenciaBusqueda;
    this.cargando = true;
    this.solicitud = this.query.query('perfiles', 'CERCANOS', {
      limite: Math.min(150, Math.max(20, this.calcularLimite(this.map.getZoom()))),
      south: b.getSouth(), north: b.getNorth(), west: b.getWest(), east: b.getEast()
    }).subscribe({
      next: (respuesta: any) => {
        if (n !== this.secuenciaBusqueda || this.destroyed) return;
        this.cargando = false;
        if (respuesta?.resultado && respuesta.resultado !== 'OK') {
          this.aviso = respuesta.mensaje || 'No se pudieron obtener los perfiles.';
          return;
        }
        this.aviso = '';
        this.perfiles = (Array.isArray(respuesta?.registros) ? respuesta.registros : Array.isArray(respuesta) ? respuesta : [])
          .filter((p: RadarPerfil) => this.coordenadasValidas(Number(p.latitud), Number(p.longitud)))
          .map((p: RadarPerfil) => ({ ...p, distanciaCalculada: this.ubicacionDisponible ? this.getDistance(this.miLat, this.miLng, Number(p.latitud), Number(p.longitud)) : null }));
        this.actualizarMarcadores();
      },
      error: () => {
        if (n !== this.secuenciaBusqueda || this.destroyed) return;
        this.cargando = false;
        this.aviso = 'No se pudo actualizar el radar. Mueve el mapa para reintentar.';
      }
    });
  }

  actualizarMarcadores(): void {
    this.marcadores.clearLayers();
    this.perfilesVisibles = this.perfiles.filter(p => !this.soloOnline || this.estaOnline(p));
    for (const perfil of this.perfilesVisibles) {
      const marker = L.circleMarker([Number(perfil.latitud), Number(perfil.longitud)], {
        radius: 9, color: '#fff', weight: 2.5,
        fillColor: this.estaOnline(perfil) ? '#2e9b6c' : '#668b7a',
        fillOpacity: 1
      });
      marker.bindPopup(this.popupPerfil(perfil), { maxWidth: 280 });
      marker.addTo(this.marcadores);
    }
  }

  centrarPerfil(perfil: RadarPerfil): void {
    if (!this.map) return;
    const lat = Number(perfil.latitud), lng = Number(perfil.longitud);
    this.map.setView([lat, lng], Math.max(this.map.getZoom(), 14));
    this.marcadores.eachLayer(layer => {
      if (layer instanceof L.CircleMarker) {
        const point = layer.getLatLng();
        if (Math.abs(point.lat - lat) < .000001 && Math.abs(point.lng - lng) < .000001) layer.openPopup();
      }
    });
  }

  private popupPerfil(u: RadarPerfil): string {
    const nombre = this.escapar(u.first_name || u.nombre || 'Usuario iGuau');
    const mascota = u.mascota?.ma_nombre ? this.escapar(u.mascota.ma_nombre) : 'Miembro de iGuau';
    const foto = this.urlSegura(u.foto_principal_thumb || u.foto_principal_grande || '');
    const slug = encodeURIComponent(String(u.slug || ''));
    const enlace = slug ? `${GlobalConstants.urlServer}perfil/${slug}` : '';
    const online = this.estaOnline(u) ? '<span class="radar-popup-online">● En línea</span>' : '';
    const distancia = this.ubicacionDisponible && u.distanciaCalculada !== null && u.distanciaCalculada !== undefined
      ? `${this.distanciaTexto(u.distanciaCalculada)} de ti` : '';
    return `<div class="radar-popup"><div class="radar-popup-head">${foto ? `<img class="radar-popup-avatar" src="${foto}" alt="">` : ''}<div><div class="radar-popup-name">${nombre}</div><div class="radar-popup-pet">${mascota}</div></div></div><div class="radar-popup-meta">${online}${online && distancia ? ' · ' : ''}${distancia}</div>${enlace ? `<a class="radar-popup-link" href="${this.escapar(enlace)}">Ver perfil</a>` : ''}</div>`;
  }

  private escapar(value: unknown): string {
    return String(value ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' } as Record<string, string>)[c]);
  }

  private urlSegura(value: string): string {
    try {
      const parsed = new URL(value, GlobalConstants.urlServer);
      return ['https:', 'http:'].includes(parsed.protocol) ? this.escapar(parsed.href) : '';
    } catch { return ''; }
  }

  private estaOnline(p: RadarPerfil): boolean {
    return p.online === true || p.online === 1 || p.online === '1';
  }

  distanciaTexto(km: number): string {
    return km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(1)} km`;
  }

  trackByPerfil(_index: number, p: RadarPerfil): string | number { return p.ID; }

  calcularLimite(zoom: number): number {
    if (zoom <= 10) return 150;
    if (zoom <= 12) return 120;
    if (zoom <= 14) return 80;
    if (zoom <= 16) return 50;
    return 35;
  }

  getDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371;
    const toRad = (d: number) => d * Math.PI / 180;
    const dLat = toRad(lat2 - lat1), dLon = toRad(lon2 - lon1);
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }
}
